import Button from 'tio-ui/components/button';
import { concat } from '@ember/helper';
import { dropTask } from 'ember-concurrency';
import Drawer from 'tio-ui/components/drawer';
import { and, or, not } from 'tio-ui/utilities';
import Modal from 'tio-ui/components/modal';
import Footer from 'tio-ui/components/footer';
import { PhCheck, PhPencilSimple, PhPlus, PhTrash, PhPencil } from 'ember-phosphor-icons';
import { formatCamelCase } from 'tio-common/utils/format';
import { htmlSafe } from '@ember/template';
import RadioGroup from 'tio-ui/components/radio-group';
import Checkbox from 'tio-ui/components/checkbox';
import Form from 'tio-ui/components/form';
import Input, { type InputType } from 'tio-ui/components/input';
import ReSelect from 'tio-ui/components/select';
import { service } from '@ember/service';
import { stateOptions } from 'tio-common/components/strategy-finder/strategy-finder-form';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { HStack, VStack, Section } from 'tio-ui/components/layout';
import Text from 'tio-ui/components/text';
import * as v from 'valibot';
import AlertBanner from 'tio-ui/components/alert-banner';
import Component from '@glimmer/component';
import errorsForValibotField from 'tio-common/helpers/errors-for-valibot-field';
import type { PslfField } from 'tio-data/data/resources/pslf-field.type';
import type { PslfFormExtension as PslfForm } from 'tio-data/data/resources/pslf-form.ext';
import type { PslfTemplateExtension as PslfTemplate } from 'tio-data/data/resources/pslf-template.ext';
import SignaturePad from 'tio-common/components/signature-pad';
import type { FormResult } from 'tio-ui/components/form';
import type { IntlService } from 'ember-intl';
import type { PointGroup } from 'signature_pad';
import type { EmployeeExtension as Employee } from 'tio-data/data/resources/employee.ext';
import type Owner from '@ember/owner';
import type PartnerService from 'tio-employee/services/partner';
import type { PslfGroup } from 'tio-data/data/resources/pslf-group.type';
import type RouterService from '@ember/routing/router-service';
// This is for types. Anything we have custom may show an error.
import type { Store as StoreService } from '@warp-drive/core';
import type { NotificationsService } from 'tio-ui/components/notifications';
import inputmask from 'tio-common/modifiers/inputmask';
import safeFormatDate from 'tio-common/helpers/safe-format-date';
import {
  batchCreateAndSignRequest,
  type BatchCreateAndSignResponse,
  verifyBorrowerAddressRequest,
  type VerifyBorrowerAddressResponse,
} from 'tio-data/requests/pslf-form';
import { signBorrowerRequest } from 'tio-data/requests/pslf-form-actions';
import { query } from '@warp-drive/utilities/json-api';
import { reloadRecord, saveRecord } from 'tio-data/requests/save-record';
import {
  findOverlappingPeriods,
  type EmploymentPeriod,
  type OverlapResult,
} from 'tio-common/utils/pslf-date-overlap';

const EXCLUDED_STATUSES = ['cancelled', 'rejected', 'abandoned', 'expired'];

interface S {
  Args: {
    template: PslfTemplate;
    employee: Employee;
  };
  Element: HTMLDivElement;
}

const boolean = Boolean;

const VALID_INPUT_TYPES = [
  'text',
  'email',
  'number',
  'password',
  'search',
  'tel',
  'date',
  'url',
] as const;

const isInputType = (fieldType: string): fieldType is InputType => {
  return VALID_INPUT_TYPES.includes(fieldType as InputType);
};

type FormV2Data = {
  [key: string]: string | number | boolean | undefined;
};

interface PastEmploymentPeriod {
  id: string;
  pslfGroup: PslfGroup;
  employmentStartDate: string;
  employmentEndDate: string;
  employmentHours: number;
}

// Schema for past period drawer form
const pastPeriodFormSchema = v.pipe(
  v.object({
    employmentStartDate: v.pipe(v.string(), v.nonEmpty('Start date is required.')),
    employmentEndDate: v.pipe(v.string(), v.nonEmpty('End date is required.')),
    employmentHours: v.pipe(
      v.number('Hours are required.'),
      v.minValue(1, 'Hours must be at least 1.'),
      v.maxValue(168, 'Hours cannot exceed 168.')
    ),
  }),
  v.forward(
    v.check((data) => {
      if (data.employmentStartDate && data.employmentEndDate) {
        const startDate = new Date(data.employmentStartDate);
        const endDate = new Date(data.employmentEndDate);
        return endDate > startDate;
      }
      return true;
    }, 'End date must be after start date.'),
    ['employmentEndDate']
  )
);

type PastPeriodFormData = {
  employmentStartDate: string;
  employmentEndDate: string;
  employmentHours: number | undefined;
};

const MAX_PAST_PERIODS = 6;

export default class PslfBorrowerForm extends Component<S> {
  @service declare store: StoreService;
  @service declare router: RouterService;
  @service declare partner: PartnerService;
  @service declare notifications: NotificationsService;
  @service declare intl: IntlService;

  @tracked hasMadeQualifyingPayments = false;
  @tracked signaturePoints: PointGroup[] = [];
  @tracked signatureImage?: string;
  @tracked selectedGroup?: PslfGroup;
  @tracked errors: Record<string, string[]> = {};
  @tracked signatureFieldErrors: string[] = [];
  @tracked currentOrPriorEmployer: 'currentEmployer' | 'priorEmployer' = 'currentEmployer';
  @tracked canSendElectronically = true;
  @tracked isPriorEmployerClosed = false;
  @tracked hasSubmitted = false;
  @tracked isOpenSignatureModal = false;
  @tracked isSsnFieldFocused = false;
  @tracked pastPeriods: PastEmploymentPeriod[] = [];
  @tracked pastPeriodErrors: Record<string, Record<string, string[]>> = {};
  @tracked isOpenPastPeriodDrawer = false;
  @tracked editingPeriodId?: string;
  @tracked editingPeriodGroup?: PslfGroup;
  @tracked editingPeriodGroupError: string[] = [];
  @tracked drawerFormData: PastPeriodFormData = {
    employmentStartDate: '',
    employmentEndDate: '',
    employmentHours: undefined,
  };
  @tracked drawerFormErrors: Record<string, string[]> = {};
  @tracked hasDrawerSubmitted = false;
  @tracked existingPeriods: EmploymentPeriod[] = [];
  @tracked overlapResults: OverlapResult[] = [];

  @tracked addressVerificationStatus?: 'valid' | 'invalid' | 'unverified' | 'verifying';
  @tracked addressVerificationSuggested?: VerifyBorrowerAddressResponse['suggestedAddress'];
  @tracked addressVerificationPending = false;
  @tracked hasBlurredAddressField = false;

  // String↔boolean adapters for reimagined RadioGroup (keys must be strings)
  get hasMadeQualifyingPaymentsKey() {
    return this.hasMadeQualifyingPayments ? 'true' : 'false';
  }
  setHasMadeQualifyingPayments = (value: string) => {
    this.hasMadeQualifyingPayments = value === 'true';
  };

  get isPriorEmployerClosedKey() {
    return this.isPriorEmployerClosed ? 'true' : 'false';
  }
  setIsPriorEmployerClosed = (value: string) => {
    this.isPriorEmployerClosed = value === 'true';
  };

  get canSendElectronicallyKey() {
    return this.canSendElectronically ? 'true' : 'false';
  }
  setCanSendElectronically = (value: string) => {
    this.canSendElectronically = value === 'true';
  };

  setCurrentOrPriorEmployer = (value: string) => {
    if (value === 'currentEmployer' || value === 'priorEmployer') {
      this.currentOrPriorEmployer = value;
    }
  };

  constructor(owner: Owner, args: S['Args']) {
    super(owner, args);
    this.setDefaultGroup();
    this.verifyPrefilledAddress();
  }

  private verifyPrefilledAddress() {
    const requiredKeys = ['borrowerAddress', 'borrowerCity', 'borrowerState', 'borrowerZip'];
    const hasCompleteAddress = requiredKeys.every((key) => {
      const field = this.args.template.pslfFields.find((f) => f.key === key);
      return field?.value;
    });

    if (hasCompleteAddress) {
      this.triggerAddressVerification();
    }
  }

  get defaultFein() {
    return this.args.employee.employeeAddition?.processed?.merged?.fein as string;
  }

  setDefaultGroup() {
    if (this.groupOptions.length === 1) {
      this.selectedGroup = this.groupOptions[0]?.item;
    } else if (this.defaultFein) {
      this.selectedGroup = this.args.employee.company.pslfGroups.find(
        (group: PslfGroup) =>
          group.employerFein.replace(/\D/g, '') === this.defaultFein.replace(/\D/g, '')
      );
    }
  }

  get formData(): FormV2Data {
    const allFields: PslfField[] = [
      ...this.args.template.borrowerFormFields.personalInfo,
      ...this.args.template.borrowerFormFields.address,
      ...this.args.template.borrowerFormFields.employerFields,
    ].filter((field: PslfField) => this.isFieldVisible(field.key));

    const data: FormV2Data = {};
    for (const field of allFields) {
      if (field.fieldType === 'number') {
        const val = field.value;
        data[field.key] = val !== '' && val != null ? Number(val) : undefined;
      } else if (field.fieldType === 'checkbox') {
        data[field.key] = Boolean(field.value);
      } else {
        data[field.key] = field.value ?? '';
      }
    }
    // SSN lives on the profile, not on a PslfField
    data['ssn'] = this.args.employee.pslfProfile.ssn || '';
    return data;
  }

  onSignatureUpdate = (points: PointGroup[], signatureUrl: string) => {
    this.signaturePoints = points;
    this.signatureImage = signatureUrl;

    if (signatureUrl) {
      this.signatureFieldErrors = [];
    }
  };

  closeSignatureModal = () => {
    this.signatureImage = undefined;
    this.signaturePoints = [];
    this.isOpenSignatureModal = false;
  };

  openSignatureModal = () => {
    this.isOpenSignatureModal = true;
  };

  captureSignature = () => {
    const hasValidSignature = !!this.signatureImage || !!this.args.employee.pslfProfile.signature;

    if (!hasValidSignature) {
      this.signatureFieldErrors = ['Signature is required.'];
      return;
    }

    const hasSignatureComplexity = this.signaturePoints.some((group) => group.points.length > 5);

    if (this.signatureImage && !hasSignatureComplexity) {
      this.signatureFieldErrors = ['Please provide a more detailed signature.'];
      return;
    }

    if (this.signatureImage) {
      this.args.employee.pslfProfile.signature = this.signatureImage;
    }

    this.signatureFieldErrors = [];
    this.isOpenSignatureModal = false;
  };

  get hasExistingSignature() {
    return !!this.args.employee.pslfProfile.signature;
  }

  get displaySignatureUrl() {
    return this.args.employee.pslfProfile.signature;
  }

  get maskedSsnValue() {
    const value = this.args.employee.pslfProfile.ssn || '';
    const digits = value.replace(/\D/g, '');

    // Show full SSN when editing/focused, masked when not
    if (this.isSsnFieldFocused || digits.length < 9) {
      return value;
    }

    // Mask first 5 digits (•••-••) and show last 4
    if (digits.length >= 9) {
      return `•••-••-${digits.slice(5, 9)}`;
    }
    return value;
  }

  onSsnFocus = (event: Event) => {
    this.isSsnFieldFocused = true;
    // maskedSsnValue swaps the masked display for the full SSN on focus; once
    // that render lands, select it so typing replaces the value. Without this,
    // the mask is already "full" and typed digits are ignored (stale SSN).
    // Only a complete SSN fills the mask, so leave partial values alone —
    // selecting those would let a stray keystroke wipe an in-progress entry.
    const digits = (this.args.employee.pslfProfile.ssn || '').replace(/\D/g, '');
    const input = event.target;
    if (digits.length === 9 && input instanceof HTMLInputElement) {
      setTimeout(() => input.select(), 0);
    }
  };

  toInputType = (fieldType: string) => {
    if (isInputType(fieldType)) {
      return fieldType;
    }

    return 'text';
  };

  onSsnBlur = () => {
    this.isSsnFieldFocused = false;
  };

  onSsnChange = (value: string) => {
    this.args.employee.pslfProfile.ssn = value;
  };

  get states() {
    return stateOptions.map((state) => ({
      label: state.name,
      key: state.abbr,
    }));
  }

  borrowerStateValue = (field: PslfField) => {
    if (field.value?.length === 2) return field.value;
    return stateOptions.find((s) => s.name === field.value)?.abbr || '';
  };

  handleBorrowerStateChange = (field: PslfField, selectedKey: string) => {
    const newStateAbr = selectedKey || '';
    this.setFieldValue(field, newStateAbr);

    if (newStateAbr && this.errors[field.key]) {
      const nextErrors = { ...this.errors };
      delete nextErrors[field.key];
      this.errors = nextErrors;
    }

    // State selection acts as a blur — trigger address verification
    this.triggerAddressVerification();
  };

  private triggerAddressVerification() {
    this.hasBlurredAddressField = true;
    const addressFields = this.buildFieldsObject(['borrower']);
    this.verifyBorrowerAddress.perform({
      addressLine1: (addressFields['borrowerAddress'] as string) || '',
      addressLine2: addressFields['borrowerApartment'] as string | undefined,
      city: (addressFields['borrowerCity'] as string) || '',
      state: (addressFields['borrowerState'] as string) || '',
      postalCode: (addressFields['borrowerZip'] as string) || '',
    });
  }

  onAddressFieldBlur = () => {
    this.triggerAddressVerification();
    if (this.hasSubmitted) {
      this.runValidation();
    }
  };

  setFieldValue = (field: PslfField, value: string | boolean | number) => {
    // Set all fields with the same key
    this.args.template.pslfFields.forEach((f) => {
      if (f.key === field.key) {
        // `value` may arrive as a boolean/number from checkbox/number inputs;
        // the field attribute is stored as a string (matching the legacy
        // setProperties behavior, which assigned the raw value unchanged).
        f.value = value as string;
      }
    });
  };

  buildFieldSchema = (field: PslfField) => {
    const { fieldType, required } = field;
    const requiredMessage = `${field.label} is required.`;
    // @ts-expect-error valibot types
    let baseSchema: v.BaseSchema;
    switch (fieldType) {
      case 'checkbox':
        baseSchema = this.buildCheckboxSchema(field, requiredMessage);
        break;
      case 'email':
        baseSchema = this.buildEmailSchema(requiredMessage);
        break;
      case 'date':
        baseSchema = this.buildDateSchema(field, requiredMessage);
        break;
      case 'number':
        baseSchema = this.buildNumberSchema(field, requiredMessage);
        break;
      case 'text':
        baseSchema = this.buildTextSchema(field, requiredMessage);
        break;
      case 'phone':
        baseSchema = this.buildPhoneSchema(field, requiredMessage);
        break;
      default:
        baseSchema = v.any();
        break;
    }
    if (!required) {
      const defaultValue =
        fieldType === 'number' || fieldType === 'date'
          ? undefined
          : fieldType === 'checkbox'
            ? false
            : '';
      return v.optional(v.nullable(baseSchema, defaultValue));
    }
    return baseSchema;
  };

  private buildCheckboxSchema = (field: PslfField, requiredMessage: string) => {
    return field.required ? v.literal(true, requiredMessage) : v.boolean();
  };

  private buildEmailSchema = (requiredMessage: string) => {
    return v.pipe(
      v.string(),
      v.nonEmpty(requiredMessage),
      v.email('Please enter a valid email address.')
    );
  };

  private buildDateSchema = (field: PslfField, requiredMessage: string) => {
    if (field.key === 'borrowerBirthDate') {
      return v.pipe(
        v.string(requiredMessage),
        v.nonEmpty(requiredMessage),
        v.isoDate('Please enter a valid date.'),
        v.check((input) => {
          const year = parseInt(input.slice(0, 4), 10);
          return year >= 1900;
        }, 'Date of birth cannot be before 1900.'),
        v.check((input) => {
          const now = new Date();
          const cutoffYear = now.getUTCFullYear() - 18;
          const cutoffMonth = String(now.getUTCMonth() + 1).padStart(2, '0');
          const cutoffDay = String(now.getUTCDate()).padStart(2, '0');
          const cutoff = `${cutoffYear}-${cutoffMonth}-${cutoffDay}`;
          return input <= cutoff;
        }, 'You must be at least 18 years old.')
      );
    }

    return v.pipe(
      v.string(requiredMessage),
      v.check((input) => {
        if (!field.required && (!input || input.length === 0)) return true;
        return input.length > 0;
      }, requiredMessage),
      v.check((input) => {
        if (!input || input.length === 0) return true;
        return /^\d{4}-\d{2}-\d{2}$/.test(input);
      }, 'Please enter a valid date.')
    );
  };

  private buildNumberSchema = (field: PslfField, requiredMessage: string) => {
    return v.pipe(
      v.number(requiredMessage),
      v.minValue(1, `${field.label} must be greater than 0.`)
    );
  };

  private buildTextSchema = (field: PslfField, requiredMessage: string) => {
    switch (field.key) {
      case 'ssn':
        return this.buildSsnSchema(requiredMessage);
      case 'borrowerZip':
        return this.buildZipSchema(requiredMessage);
      case 'employerFein':
        return this.buildFeinSchema(requiredMessage);
      default:
        return this.buildGenericTextSchema(field, requiredMessage);
    }
  };

  private buildSsnSchema = (requiredMessage: string) => {
    return v.pipe(
      v.string(requiredMessage),
      v.nonEmpty(requiredMessage),
      v.regex(/^(\d{3}-\d{2}-\d{4}|•••-••-\d{4})$/, 'Please enter a valid social security number')
    );
  };

  private buildZipSchema = (requiredMessage: string) => {
    return v.pipe(
      v.string(requiredMessage),
      v.nonEmpty(requiredMessage),
      v.regex(/^\d{5}$/, 'Please enter a valid 5-digit ZIP code.')
    );
  };

  private buildFeinSchema = (requiredMessage: string) => {
    return v.pipe(
      v.string(requiredMessage),
      v.nonEmpty(requiredMessage),
      v.regex(/^\d{2}-?\d{7}$/, 'FEIN must be 9 digits')
    );
  };

  private buildGenericTextSchema = (field: PslfField, requiredMessage: string) => {
    return v.pipe(
      v.string(requiredMessage),
      v.check((input) => (field.required ? !!input.length : true), requiredMessage)
    );
  };

  private buildPhoneSchema = (field: PslfField, requiredMessage: string) => {
    return v.pipe(
      v.string(requiredMessage),
      v.check((input) => {
        if (!field.required && (!input || input.length === 0)) return true;
        // Accept any format with 10 digits or 11 digits starting with 1
        const digits = input.replace(/\D/g, '');
        return digits.length === 10 || (digits.length === 11 && digits.startsWith('1'));
      }, 'Please enter a valid US phone number.')
    );
  };

  get formSchema() {
    const allFields: PslfField[] = [
      ...this.args.template.borrowerFormFields.personalInfo,
      ...this.args.template.borrowerFormFields.address,
      ...this.args.template.borrowerFormFields.employerFields,
    ].filter((field: PslfField) => this.isFieldVisible(field.key));
    const schemaDefinition = Object.fromEntries(
      allFields.map((field: PslfField) => [field.key, this.buildFieldSchema(field)])
    );

    return v.object(schemaDefinition);
  }

  runValidation = () => {
    const data = this.formData;
    const result = v.safeParse(this.formSchema, data);

    if (result.success) {
      this.errors = {};
    } else {
      const validationErrors: Record<string, string[]> = {};

      for (const issue of result.issues) {
        const path = issue.path?.[0]?.key as string;
        if (path) {
          if (!validationErrors[path]) {
            validationErrors[path] = [];
          }
          validationErrors[path].push(issue.message);
        }
      }
      this.errors = validationErrors;
    }

    const profileSsn = this.args.employee.pslfProfile.ssn || '';
    const ssnDigits = profileSsn.replace(/\D/g, '');
    const isValidSsn = ssnDigits.length === 9;
    if (!isValidSsn) {
      this.errors = {
        ...this.errors,
        ssn: [...(this.errors.ssn || []), 'Please enter a valid social security number'],
      };
    }

    if (this.currentOrPriorEmployer === 'currentEmployer' && !this.selectedGroup) {
      this.errors = { employmentGroup: ['Please select your employer group.'], ...this.errors };
    } else {
      delete this.errors.employmentGroup;
    }
  };

  updateFormField = (fieldKey: string, value: unknown) => {
    const field = this.args.template.pslfFields.find((f) => f.key === fieldKey);
    if (field) {
      this.setFieldValue(field, (value as string | boolean | number) ?? '');
    }
    if (this.hasSubmitted) {
      this.runValidation();
    }
  };

  updateDateField = (field: PslfField, value: string) => {
    // Date inputs return ISO date strings (YYYY-MM-DD) directly
    this.setFieldValue(field, value || '');
    if (this.hasSubmitted) {
      this.runValidation();
    }
  };

  updatePhoneField = (field: PslfField, value: string) => {
    // Store raw value during editing; normalization happens on blur
    this.setFieldValue(field, value || '');
    if (this.hasSubmitted) {
      this.runValidation();
    }
  };

  private normalizePhoneField = (field: PslfField) => {
    // Normalize phone to +1XXXXXXXXXX format
    const value = (field.value as string) || '';
    const digits = value.replace(/\D/g, '');
    if (digits.length === 10) {
      this.setFieldValue(field, `+1${digits}`);
    } else if (digits.length === 11 && digits.startsWith('1')) {
      this.setFieldValue(field, `+${digits}`);
    }
  };

  onPhoneBlur = (field: PslfField) => {
    this.normalizePhoneField(field);
    if (this.hasSubmitted) {
      this.runValidation();
    }
  };

  updateCheckboxField = (field: PslfField, checked: boolean) => {
    this.setFieldValue(field, checked);
    if (this.hasSubmitted) {
      this.runValidation();
    }
  };

  onFormSubmit = (_result: FormResult<FormV2Data>) => {
    this.hasSubmitted = true;

    // Normalize any phone fields before validation/submit
    const allFields: PslfField[] = [
      ...this.args.template.borrowerFormFields.personalInfo,
      ...this.args.template.borrowerFormFields.address,
      ...this.args.template.borrowerFormFields.employerFields,
    ];
    for (const field of allFields) {
      if (field.fieldType === 'phone') {
        this.normalizePhoneField(field);
      }
    }

    this.runValidation();

    // Validate past periods if any exist
    const pastPeriodsValid = this.pastPeriods.length === 0 || this.validatePastPeriods();

    if (!this.hasExistingSignature) {
      this.signatureFieldErrors = ['Please provide a signature before submitting.'];
    } else {
      this.signatureFieldErrors = [];
    }

    const allErrors = {
      ...this.errors,
      ...(this.signatureFieldErrors.length > 0 ? { signature: this.signatureFieldErrors } : {}),
    };

    if (Object.keys(allErrors).length === 0 && pastPeriodsValid) {
      this.onSubmit.perform();
    } else {
      // Scroll to the first error field
      const firstErrorKey = Object.keys(allErrors)[0];
      const el = document.querySelector<HTMLElement>(`[name='${firstErrorKey}']`);

      if (el) {
        // Use the parent select container for scroll target if available,
        // since the named element may be visually hidden.
        const scrollTarget = el.closest('[data-test-select]') ?? el;
        scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  private buildFieldsObject = (
    keyPrefixes: string[]
  ): { [k: string]: string | boolean | number } => {
    return Object.fromEntries(
      this.args.template.pslfFields
        .filter((field) => {
          const matchesPrefix = keyPrefixes.some((prefix) => field.key.startsWith(prefix));
          return (
            matchesPrefix &&
            field.fieldType !== 'signature' &&
            field.value &&
            this.isFieldVisible(field.key)
          );
        })
        .map((field) => [field.key, field.value])
    );
  };

  get hasSuggestedAddress() {
    return !!(this.addressVerificationSuggested && this.addressVerificationSuggested.addressLine1);
  }

  get formattedSuggestedAddress() {
    const s = this.addressVerificationSuggested;
    if (!s) return '';
    const parts = [
      s.addressLine1,
      s.addressLine2,
      [s.city, [s.state, s.postalCode].filter(Boolean).join(' ')].filter(Boolean).join(', '),
    ].filter(Boolean);
    return parts.join(', ');
  }

  acceptSuggestedAddress = () => {
    const suggested = this.addressVerificationSuggested;
    if (!suggested) return;

    const fieldMap: Record<string, string> = {
      borrowerAddress: suggested.addressLine1 ?? '',
      borrowerApartment: suggested.addressLine2 ?? '',
      borrowerCity: suggested.city ?? '',
      borrowerState: suggested.state ?? '',
      borrowerZip: suggested.postalCode ?? '',
    };

    for (const [key, value] of Object.entries(fieldMap)) {
      const field = this.args.template.pslfFields.find((f) => f.key === key);
      if (field) {
        this.setFieldValue(field, value);
      }
    }

    this.addressVerificationStatus = 'valid';
    this.addressVerificationSuggested = undefined;
    const wasSubmitting = this.addressVerificationPending;
    this.addressVerificationPending = false;
    if (wasSubmitting) {
      this.onSubmit.perform();
    }
  };

  dismissSuggestion = () => {
    this.addressVerificationSuggested = undefined;
    const wasSubmitting = this.addressVerificationPending;
    this.addressVerificationPending = false;
    if (wasSubmitting) {
      this.onSubmit.perform();
    }
  };

  private verifyBorrowerAddress = dropTask(
    async (address: {
      addressLine1: string;
      addressLine2?: string;
      city: string;
      state: string;
      postalCode: string;
    }) => {
      const sanitizedZip = address.postalCode.replace(/\D/g, '').slice(0, 5);
      const state = address.state?.trim();

      if (
        !address.addressLine1?.trim() ||
        !address.city?.trim() ||
        !state ||
        sanitizedZip.length !== 5
      ) {
        this.addressVerificationStatus = undefined;
        this.addressVerificationSuggested = undefined;
        return;
      }

      this.addressVerificationStatus = 'verifying';
      this.addressVerificationSuggested = undefined;

      try {
        const { content } = (await this.store.request(
          verifyBorrowerAddressRequest({
            address: {
              addressLine1: address.addressLine1,
              addressLine2: address.addressLine2,
              city: address.city,
              state: state,
              postalCode: sanitizedZip,
              country: 'US',
            },
          })
        )) as { content: VerifyBorrowerAddressResponse };

        this.addressVerificationStatus = content.status;
        this.addressVerificationSuggested = content.suggestedAddress;
      } catch (error) {
        console.error('Error verifying borrower address', error);
        this.addressVerificationStatus = 'unverified';
        this.addressVerificationSuggested = undefined;
      }
    }
  );

  onSubmit = dropTask(async () => {
    const borrowerFields = this.buildFieldsObject(['borrower']);

    const borrowerAddress = {
      addressLine1: (borrowerFields['borrowerAddress'] as string) || '',
      addressLine2: borrowerFields['borrowerApartment'] as string | undefined,
      city: (borrowerFields['borrowerCity'] as string) || '',
      state: (borrowerFields['borrowerState'] as string) || '',
      postalCode: (borrowerFields['borrowerZip'] as string) || '',
    };

    // Skip re-verification if user already resolved a suggestion
    if (!this.addressVerificationPending) {
      await this.verifyBorrowerAddress.perform(borrowerAddress);
    }

    // Pause submission if there's a suggested correction the user hasn't resolved
    if (this.hasSuggestedAddress && !this.addressVerificationPending) {
      this.addressVerificationPending = true;
      return;
    }

    if (this.addressVerificationStatus && this.addressVerificationStatus !== 'verifying') {
      borrowerFields['borrowerAddressVerificationStatus'] = this.addressVerificationStatus;
    }

    const signDate = new Date().toISOString().split('T')[0] || ''; // YYYY-MM-DD
    borrowerFields['borrowerNonPartnerForm'] = this.isNonPartnerOrPriorEmployer;
    borrowerFields['borrowerSignDate'] = signDate;
    borrowerFields['borrowerPriorEmployerClosed'] = this.isPriorEmployerClosed;
    borrowerFields['borrowerPrintAndMail'] =
      !this.canSendElectronically || this.isPriorEmployerClosed;

    const employerFields = this.buildFieldsObject(['employer', 'employment']);
    employerFields['employmentContinues'] =
      !this.isNonPartnerOrPriorEmployer && !employerFields['employmentEndDate'];
    if (!this.isNonPartnerOrPriorEmployer) {
      employerFields['employmentStatus'] = 'fullTime';
    }

    // Determine if pre-approved
    const hours = employerFields['employmentHours'] as number;
    const employeeAdditionHours = Number(
      this.args.employee.employeeAddition?.processed?.merged?.['hoursWorkedPerWeek']
    );
    // Only care about hours changing if not full-time
    const isFullTime = employeeAdditionHours >= 30 && hours >= 30;
    const isHoursChanged =
      Number.isNaN(employeeAdditionHours) ||
      (!isFullTime && Math.abs(hours - employeeAdditionHours) > 0.5);
    const isStartDateChanged =
      employerFields['employmentStartDate'] !==
      this.args.employee.employeeAddition?.processed?.merged?.['employmentStartDate'];
    const isPreApproved =
      !!this.selectedGroup?.autoSign && !isHoursChanged && hours >= 30 && !isStartDateChanged;
    if (isPreApproved) {
      employerFields['employerSignDate'] = signDate;
    }

    try {
      if (this.args.employee.pslfProfile.hasDirtyAttributes) {
        // WarpDrive mutation builder (Phase 2.7) — replaces record.save().
        await saveRecord(this.store, this.args.employee.pslfProfile);
      }

      if (this.pastPeriods.length > 0 && this.currentOrPriorEmployer === 'currentEmployer') {
        const { content: result } = (await this.store.request(
          batchCreateAndSignRequest({
            pslfProfileId: this.args.employee.pslfProfile.id!,
            pslfTemplateId: this.args.template.id!,
            primaryForm: {
              formType: this.currentOrPriorEmployer,
              pslfGroupId: this.isNonPartnerOrPriorEmployer ? undefined : this.selectedGroup?.id,
              isNonPartner: this.isNonPartnerOrPriorEmployer,
              isPreApproved,
              borrowerFields,
              employerFields,
            },
            pastPeriods: this.pastPeriods.map((period) => ({
              pslfGroupId: period.pslfGroup.id ?? undefined,
              employerFields: {
                employmentStartDate: period.employmentStartDate,
                employmentEndDate: period.employmentEndDate,
                employmentHours: period.employmentHours,
                employmentStatus: 'fullTime',
              },
            })),
          })
        )) as { content: BatchCreateAndSignResponse };

        this.router.replaceWith('authenticated.pslf.form.success', result.primaryFormId, {
          queryParams: {
            pastPeriodIds: result.pastPeriodFormIds.length
              ? result.pastPeriodFormIds.join(',')
              : undefined,
          },
        });
      } else {
        const pslfGroup = this.isNonPartnerOrPriorEmployer ? undefined : this.selectedGroup;

        const formRecord = this.store.createRecord<PslfForm>('pslf-form', {
          formType: this.currentOrPriorEmployer,
          pslfProfile: this.args.employee.pslfProfile,
          pslfGroup,
          isNonPartner: this.isNonPartnerOrPriorEmployer,
          // @ts-expect-error: API fills in the rest of the fields
          borrowerFields,
          // @ts-expect-error: API fills in the rest of the fields
          employerFields,
        });

        formRecord.pslfTemplate = this.args.template;
        formRecord.isPreApproved = isPreApproved;

        // WarpDrive save helper (Phase 2.7) — replaces
        // formRecord.save({ adapterOptions: { useV2: true } }); the legacy
        // PslfFormAdapter `useV2` create POSTed to /api/v2/pslf-forms, so route
        // the builder through the `api/v2` namespace (established pattern).
        await saveRecord(this.store, formRecord, { namespace: 'api/v2' });

        // Legacy PslfFormAdapter#signBorrower PATCHed /sign-borrower with
        // X-Reindex-Mode: sync then reloaded the form and returned it; preserve
        // both steps (the form id is stable across the reload). /sign-borrower
        // goes through perform_action → `head 204` (renders NO resource), so the
        // builder does NOT refresh the cache — keep the reload via the helper.
        await this.store.request(signBorrowerRequest(formRecord.id!));
        await reloadRecord(this.store, formRecord);

        this.router.replaceWith('authenticated.pslf.form.success', formRecord.id);
      }
    } catch (error) {
      console.error('Error submitting PSLF form ', error);
      const errorMessage = this.intl.t('pslf.start_new.form_submitted_error');
      this.notifications.add(errorMessage, { appearance: 'error' });
    }
  });

  get groupOptions() {
    return this.args.employee.company.pslfGroups.map((group: PslfGroup) => ({
      ...group,
      item: group,
      id: group.id,
      label: `${group.employerCommonName ? group.employerCommonName + ' - ' : ''}${
        group.employerFein
      }`,
    }));
  }

  selectGroup = (groupId: string) => {
    this.selectedGroup = this.args.employee.company.pslfGroups.find(
      (group: PslfGroup) => group.id === groupId
    );

    if (this.selectedGroup && this.errors.employmentGroup) {
      const errorsWithoutEmploymentGroup = { ...this.errors };
      delete errorsWithoutEmploymentGroup.employmentGroup;
      this.errors = errorsWithoutEmploymentGroup;
    }
  };

  openAddPeriodDrawer = () => {
    if (this.pastPeriods.length >= MAX_PAST_PERIODS) return;
    this.editingPeriodId = crypto.randomUUID();
    this.editingPeriodGroup = undefined;
    this.editingPeriodGroupError = [];
    this.drawerFormData = {
      employmentStartDate: '',
      employmentEndDate: '',
      employmentHours: undefined,
    };
    this.overlapResults = [];
    this.isOpenPastPeriodDrawer = true;
    this.fetchExistingForms();
  };

  openEditPeriodDrawer = (period: PastEmploymentPeriod) => {
    this.editingPeriodId = period.id;
    this.editingPeriodGroup = period.pslfGroup;
    this.editingPeriodGroupError = [];
    this.drawerFormData = {
      employmentStartDate: period.employmentStartDate || '',
      employmentEndDate: period.employmentEndDate || '',
      employmentHours: period.employmentHours || 0,
    };
    this.overlapResults = [];
    this.isOpenPastPeriodDrawer = true;
    this.fetchExistingForms().then(() => this.checkForOverlaps());
  };

  closePastPeriodDrawer = () => {
    this.drawerFormData = {
      employmentStartDate: '',
      employmentEndDate: '',
      employmentHours: undefined,
    };
    this.drawerFormErrors = {};
    this.hasDrawerSubmitted = false;
    this.isOpenPastPeriodDrawer = false;
    this.editingPeriodId = undefined;
    this.editingPeriodGroup = undefined;
    this.editingPeriodGroupError = [];
    this.overlapResults = [];
  };

  removePastPeriod = (id: string) => {
    this.pastPeriods = this.pastPeriods.filter((p) => p.id !== id);
    // Remove errors for this period
    const newErrors = { ...this.pastPeriodErrors };
    delete newErrors[id];
    this.pastPeriodErrors = newErrors;
  };

  selectDrawerGroup = (groupId: string) => {
    const group = this.args.employee.company.pslfGroups.find((g: PslfGroup) => g.id === groupId);
    this.editingPeriodGroup = group;
    this.editingPeriodGroupError = [];
  };

  updateDrawerField = (field: keyof PastPeriodFormData, value: unknown) => {
    if (field === 'employmentHours') {
      const numValue = value === '' || value == null ? undefined : Number(value);
      this.drawerFormData = { ...this.drawerFormData, [field]: numValue };
    } else {
      this.drawerFormData = { ...this.drawerFormData, [field]: value as string };
    }

    if (this.hasDrawerSubmitted) {
      this.validateDrawerForm();
    }
    if (field === 'employmentStartDate' || field === 'employmentEndDate') {
      this.checkForOverlaps();
    }
  };

  validateDrawerForm = (): v.InferOutput<typeof pastPeriodFormSchema> | null => {
    const validationResult = v.safeParse(pastPeriodFormSchema, this.drawerFormData);

    if (validationResult.success) {
      this.drawerFormErrors = {};
      return validationResult.output;
    } else {
      const validationErrors: Record<string, string[]> = {};
      for (const issue of validationResult.issues) {
        const path = issue.path?.[0]?.key as string;
        if (path) {
          if (!validationErrors[path]) {
            validationErrors[path] = [];
          }
          validationErrors[path].push(issue.message);
        }
      }
      this.drawerFormErrors = validationErrors;
      return null;
    }
  };

  get todayDateString() {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  // --- Overlap detection ---

  fetchExistingForms = async () => {
    const employeeId = this.args.employee.id;
    if (!employeeId) return;

    try {
      // WarpDrive builder read (Phase 2.4) — replaces store.query;
      // flattened bracket keys satisfy the builder's QueryParamsSource type.
      const { content } = (await this.store.request(
        query<PslfForm>('pslf-form', {
          'filter[employee-id]': employeeId,
          'page[limit]': 50,
          'page[offset]': 0,
        })
      )) as { content: { data: PslfForm[] } };
      const forms = content.data;

      this.existingPeriods = forms
        .filter((f: PslfForm) => !EXCLUDED_STATUSES.includes(f.status))
        .map((f: PslfForm) => ({
          startDate:
            f.employerFields?.['employment-start-date'] ||
            f.borrowerFields?.['borrower-employment-start-date'] ||
            '',
          endDate:
            f.employerFields?.['employment-end-date'] ||
            f.borrowerFields?.['borrower-employment-end-date'] ||
            '',
          employerName:
            f.borrowerFields?.['borrower-employer-name'] ||
            f.employerFields?.['employer-name'] ||
            '',
          formId: f.id,
          status: f.status,
        }))
        .filter((p: EmploymentPeriod) => p.startDate);
      this.checkForOverlaps();
    } catch {
      this.existingPeriods = [];
    }
  };

  checkForOverlaps = () => {
    const { employmentStartDate, employmentEndDate } = this.drawerFormData;
    if (!employmentStartDate || !employmentEndDate) {
      this.overlapResults = [];
      return;
    }

    const candidate: EmploymentPeriod = {
      startDate: employmentStartDate,
      endDate: employmentEndDate || undefined,
    };

    // Other local past periods (excluding the one being edited)
    const localPeriods: EmploymentPeriod[] = this.pastPeriods
      .filter((p) => p.id !== this.editingPeriodId)
      .map((p) => ({
        startDate: p.employmentStartDate,
        endDate: p.employmentEndDate,
        employerName: p.pslfGroup.employerLegalName || '',
      }));

    const allPeriods = [...localPeriods, ...this.existingPeriods];
    this.overlapResults = findOverlappingPeriods(candidate, allPeriods);
  };

  handleDrawerFormSubmit = () => {
    this.hasDrawerSubmitted = true;

    // Validate group separately since it's not part of the form schema
    if (!this.editingPeriodGroup) {
      this.editingPeriodGroupError = [
        String(this.intl.t('pslf.start_new.errors.select_employer_group')),
      ];
    } else {
      this.editingPeriodGroupError = [];
    }

    const parsedData = this.validateDrawerForm();
    const group = this.editingPeriodGroup;

    if (!parsedData || !group || !this.editingPeriodId) {
      return;
    }

    const newPeriod: PastEmploymentPeriod = {
      id: this.editingPeriodId,
      pslfGroup: group,
      employmentStartDate: parsedData.employmentStartDate,
      employmentEndDate: parsedData.employmentEndDate,
      employmentHours: parsedData.employmentHours,
    };

    const existingIndex = this.pastPeriods.findIndex((p) => p.id === this.editingPeriodId);

    if (existingIndex >= 0) {
      this.pastPeriods = this.pastPeriods.map((p) =>
        p.id === this.editingPeriodId ? newPeriod : p
      );
    } else {
      this.pastPeriods = [...this.pastPeriods, newPeriod];
    }

    this.closePastPeriodDrawer();
  };

  get fullPastPeriodSchema() {
    return v.pipe(
      v.object({
        id: v.string(),
        pslfGroup: v.pipe(
          v.any(),
          v.check((group) => !!group, 'Please select an employer group.')
        ),
        employmentStartDate: v.pipe(
          v.string('Start date is required.'),
          v.nonEmpty('Start date is required.')
        ),
        employmentEndDate: v.pipe(
          v.string('End date is required.'),
          v.nonEmpty('End date is required.')
        ),
        employmentHours: v.pipe(
          v.number('Hours are required.'),
          v.minValue(1, 'Hours must be at least 1.'),
          v.maxValue(168, 'Hours cannot exceed 168.')
        ),
      }),
      v.forward(
        v.check((data) => {
          if (data.employmentStartDate && data.employmentEndDate) {
            const start = new Date(data.employmentStartDate);
            const end = new Date(data.employmentEndDate);
            return end > start;
          }
          return true;
        }, 'End date must be after start date.'),
        ['employmentEndDate']
      )
    );
  }

  validatePastPeriods = () => {
    const errors: Record<string, Record<string, string[]>> = {};
    let isValid = true;

    for (const period of this.pastPeriods) {
      const result = v.safeParse(this.fullPastPeriodSchema, period);

      if (!result.success) {
        const periodErrors: Record<string, string[]> = {};

        for (const issue of result.issues) {
          const path = issue.path?.[0]?.key as string;
          if (path) {
            if (!periodErrors[path]) {
              periodErrors[path] = [];
            }
            periodErrors[path].push(issue.message);
          }
        }

        if (Object.keys(periodErrors).length > 0) {
          errors[period.id] = periodErrors;
          isValid = false;
        }
      }
    }

    this.pastPeriodErrors = errors;
    return isValid;
  };

  get showPastPeriodsSection() {
    // TODO: how to handle non-partner employers?
    return this.currentOrPriorEmployer === 'currentEmployer' && !this.isNonPartnerEmployer;
  }

  get canAddMorePeriods() {
    return this.pastPeriods.length < MAX_PAST_PERIODS;
  }

  get isEditingExistingPeriod() {
    return this.pastPeriods.some((p) => p.id === this.editingPeriodId);
  }

  get showGroupSelect() {
    return (
      !this.isPriorEmployer &&
      !this.isNonPartnerEmployer &&
      this.groupOptions.length > 1 &&
      !this.defaultFein
    );
  }

  get isNonPartnerEmployer() {
    const employerPslfModule = this.partner.settingModules?.pslf;
    return !employerPslfModule || employerPslfModule.isNonPartnerEmployer;
  }

  get isPriorEmployer() {
    return this.currentOrPriorEmployer === 'priorEmployer';
  }

  get isNonPartnerOrPriorEmployer() {
    return this.isNonPartnerEmployer || this.isPriorEmployer;
  }

  get isEmployerContactFieldsVisible() {
    return this.isNonPartnerOrPriorEmployer && !this.isPriorEmployerClosed;
  }

  isFieldVisible = (fieldKey: string): boolean => {
    switch (fieldKey) {
      case 'employerName':
        return this.isNonPartnerOrPriorEmployer;
      case 'employerFein':
        return this.isNonPartnerOrPriorEmployer && this.isPriorEmployerClosed;
      case 'employerWebsite':
      case 'employerContactName':
      case 'employerContactPhoneNumber':
      case 'employerContactTitle':
        return this.isEmployerContactFieldsVisible;
      case 'employerContactEmail':
        return this.isEmployerContactFieldsVisible && this.canSendElectronically;
      case 'employmentEndDate':
        return this.isPriorEmployer;
      default:
        return true;
    }
  };

  <template>
    <Form @data={{this.formData}} @onSubmit={{this.onFormSubmit}} data-test="pslf-form">
      <VStack>
        <Section class="border-0">
          <:header>{{t "pslf.start_new.pslf_form_header"}}</:header>
          <:body>
            {{t "pslf.start_new.pslf_preamble"}}
          </:body>
        </Section>
        {{#each-in @template.borrowerFormFields as |formGroup fields|}}
          <Section class="border-0">
            <:header>
              {{#if (eq formGroup "personalInfo")}}
                {{t "pslf.start_new.form_section_personal_info"}}
              {{else if (eq formGroup "address")}}
                {{t "pslf.start_new.form_section_address"}}
              {{else if (eq formGroup "employerFields")}}
                {{t "pslf.start_new.form_section_employer_fields"}}
              {{else}}
                {{formatCamelCase formGroup}}
              {{/if}}
            </:header>
            <:body>
              <VStack>
                <div><span class="text-danger font-semibold mr-1">*</span>{{t
                    "pslf.start_new.required_items"
                  }}</div>
                {{#if (eq formGroup "employerFields")}}
                  <RadioGroup
                    @label={{t "pslf.start_new.select_employer"}}
                    @onChange={{this.setCurrentOrPriorEmployer}}
                    @value={{this.currentOrPriorEmployer}}
                    data-test="radio-select-employer"
                    @options={{array
                      (hash
                        key="currentEmployer"
                        label=(t "pslf.start_new.current")
                        dataTestId="radio-current-employer"
                      )
                      (hash
                        key="priorEmployer"
                        label=(t "pslf.start_new.former")
                        dataTestId="radio-prior-employer"
                      )
                    }}
                  />
                  {{#if this.showGroupSelect}}
                    <ReSelect
                      @name="employmentGroup"
                      @label="Select Employer"
                      @items={{this.groupOptions}}
                      @selected={{if this.selectedGroup this.selectedGroup.id}}
                      @onChange={{this.selectGroup}}
                      @clearable={{false}}
                      data-test="select-employer"
                      @errors={{errorsForValibotField
                        "employmentGroup"
                        schemaErrors=this.errors
                        hasSubmitted=this.hasSubmitted
                      }}
                    />
                  {{/if}}
                {{/if}}
                {{#each fields as |field|}}
                  {{#if (eq field.fieldType "checkbox")}}
                    {{#if (eq field.key "borrowerWantsForbearance")}}
                      <RadioGroup
                        @label={{t "pslf.workflow.reason_submitting.have_you_made"}}
                        @value={{this.hasMadeQualifyingPaymentsKey}}
                        @onChange={{this.setHasMadeQualifyingPayments}}
                        data-test="radio-borrower-wants-forbearance"
                        @options={{array
                          (hash key="false" label=(t "common.no") dataTestId="radio-no")
                          (hash key="true" label=(t "common.yes") dataTestId="radio-yes")
                        }}
                      />
                      {{#if this.hasMadeQualifyingPayments}}
                        <p>
                          {{t "pslf.workflow.reason_submitting.forbearance.tooltip"}}
                        </p>
                        <Checkbox
                          {{! @glint-expect-error: label arg doesn't like SafeString}}
                          @label={{htmlSafe
                            (t "pslf.workflow.reason_submitting.forbearance.checkbox")
                          }}
                          @checked={{boolean field.value}}
                          @onChange={{fn this.updateCheckboxField field}}
                          @errors={{errorsForValibotField
                            field.key
                            schemaErrors=this.errors
                            hasSubmitted=this.hasSubmitted
                          }}
                          data-test="checkbox-forbearance"
                        />
                      {{/if}}
                    {{else if
                      (and (eq field.key "borrowerPriorEmployerClosed") this.isPriorEmployer)
                    }}
                      <RadioGroup
                        @label={{field.label}}
                        @onChange={{this.setIsPriorEmployerClosed}}
                        @value={{this.isPriorEmployerClosedKey}}
                        data-test="radio-select-partner-closed-or-refused"
                        @options={{array
                          (hash key="false" label=(t "common.no") dataTestId="radio-no")
                          (hash key="true" label=(t "common.yes") dataTestId="radio-yes")
                        }}
                      />
                      {{#if this.isPriorEmployerClosed}}
                        <AlertBanner @intent="info">{{htmlSafe
                            (t "pslf.workflow.print_and_mail_alert")
                          }}</AlertBanner>
                      {{/if}}
                    {{/if}}
                  {{else if (eq field.key "borrowerState")}}
                    <ReSelect
                      @name={{field.key}}
                      @searchable={{true}}
                      @onChange={{fn this.handleBorrowerStateChange field}}
                      @items={{this.states}}
                      @selected={{this.borrowerStateValue field}}
                      @label={{field.label}}
                      @clearable={{true}}
                      @errors={{errorsForValibotField
                        field.key
                        schemaErrors=this.errors
                        hasSubmitted=this.hasSubmitted
                      }}
                      @placeholder="Select a state"
                      data-test="select-borrower-state"
                    />
                  {{else}}
                    {{#if
                      (and
                        (eq field.key "employerContactEmail") this.isEmployerContactFieldsVisible
                      )
                    }}
                      <RadioGroup
                        @label={{t "pslf.workflow_prior_employer.send_electronically"}}
                        @onChange={{this.setCanSendElectronically}}
                        @value={{this.canSendElectronicallyKey}}
                        data-test="radio-select-can-send-electronically"
                        @options={{array
                          (hash
                            key="false"
                            label=(t "pslf.workflow.no_employer_email")
                            dataTestId="radio-no-employer-email"
                          )
                          (hash
                            key="true"
                            label=(t "pslf.workflow.yes_employer_email")
                            dataTestId="radio-yes-employer-email"
                          )
                        }}
                      />
                      {{#if this.canSendElectronically}}
                        <p>{{t "pslf.workflow.please_provide"}}</p>
                      {{else}}
                        <AlertBanner @intent="info">{{htmlSafe
                            (t "pslf.workflow.print_and_mail_alert")
                          }}</AlertBanner>
                      {{/if}}
                    {{/if}}
                    {{#if (this.isFieldVisible field.key)}}
                      {{#if (eq field.key "ssn")}}
                        <Input
                          @name={{field.key}}
                          @type="text"
                          @label={{field.label}}
                          @value={{this.maskedSsnValue}}
                          @errors={{errorsForValibotField
                            field.key
                            schemaErrors=this.errors
                            hasSubmitted=this.hasSubmitted
                          }}
                          placeholder="###-##-####"
                          @onChange={{this.onSsnChange}}
                          @isRequired={{true}}
                          {{on "focus" this.onSsnFocus}}
                          {{on "blur" this.onSsnBlur}}
                          {{inputmask
                            mask="MMM-MM-9999"
                            definitions=(hash M=(hash validator="[0-9•]"))
                          }}
                          data-test={{concat "input-" field.key}}
                        />
                      {{else if (eq field.key "borrowerZip")}}
                        <Input
                          @name={{field.key}}
                          @type="text"
                          @label={{field.label}}
                          @value={{field.value}}
                          @onChange={{fn this.updateFormField field.key}}
                          @errors={{errorsForValibotField
                            field.key
                            schemaErrors=this.errors
                            hasSubmitted=this.hasSubmitted
                          }}
                          @isRequired={{field.required}}
                          placeholder="#####"
                          {{inputmask mask="99999"}}
                          {{on "blur" this.onAddressFieldBlur}}
                          data-test={{concat "input-" field.key}}
                        />
                      {{else if (eq field.key "employerFein")}}
                        <Input
                          @name={{field.key}}
                          @type="text"
                          @label={{field.label}}
                          @value={{field.value}}
                          @onChange={{fn this.updateFormField field.key}}
                          @errors={{errorsForValibotField
                            field.key
                            schemaErrors=this.errors
                            hasSubmitted=this.hasSubmitted
                          }}
                          @isRequired={{field.required}}
                          placeholder="##-#######"
                          {{inputmask mask="99-9999999"}}
                          data-test={{concat "input-" field.key}}
                        />
                      {{else if (eq field.fieldType "phone")}}
                        <Input
                          @name={{field.key}}
                          @type="tel"
                          @label={{field.label}}
                          @value={{field.value}}
                          @onChange={{fn this.updatePhoneField field}}
                          @onBlur={{fn this.onPhoneBlur field}}
                          @errors={{errorsForValibotField
                            field.key
                            schemaErrors=this.errors
                            hasSubmitted=this.hasSubmitted
                          }}
                          @isRequired={{field.required}}
                          placeholder="+1 (###) ###-####"
                          {{inputmask mask="+1 (999) 999-9999"}}
                          data-test={{concat "input-" field.key}}
                        />
                      {{else if (eq field.fieldType "date")}}
                        <Input
                          @name={{field.key}}
                          @type="date"
                          @label={{field.label}}
                          @value={{field.value}}
                          @onChange={{fn this.updateDateField field}}
                          @errors={{errorsForValibotField
                            field.key
                            schemaErrors=this.errors
                            hasSubmitted=this.hasSubmitted
                          }}
                          @isRequired={{field.required}}
                          data-test={{concat "input-" field.key}}
                        />
                      {{else if
                        (or
                          (eq field.key "borrowerAddress")
                          (eq field.key "borrowerApartment")
                          (eq field.key "borrowerCity")
                        )
                      }}
                        <Input
                          @name={{field.key}}
                          @type={{this.toInputType field.fieldType}}
                          @label={{field.label}}
                          @value={{field.value}}
                          @onChange={{fn this.updateFormField field.key}}
                          @errors={{errorsForValibotField
                            field.key
                            schemaErrors=this.errors
                            hasSubmitted=this.hasSubmitted
                          }}
                          @isRequired={{field.required}}
                          {{on "blur" this.onAddressFieldBlur}}
                          data-test={{concat "input-" field.key}}
                        />
                      {{else}}
                        {{! At this point field.fieldType is text, email, or number }}
                        <Input
                          @name={{field.key}}
                          @type={{this.toInputType field.fieldType}}
                          @label={{field.label}}
                          @value={{field.value}}
                          @onChange={{fn this.updateFormField field.key}}
                          @errors={{errorsForValibotField
                            field.key
                            schemaErrors=this.errors
                            hasSubmitted=this.hasSubmitted
                          }}
                          @isRequired={{field.required}}
                          data-test={{concat "input-" field.key}}
                        />
                      {{/if}}
                    {{/if}}
                  {{/if}}
                {{/each}}

                {{#if (eq formGroup "address")}}
                  {{#if (eq this.addressVerificationStatus "verifying")}}
                    <Text @variant="muted" data-test="address-verification-verifying">{{t
                        "pslf.start_new.address_verification.verifying"
                      }}</Text>
                  {{else if
                    (and (eq this.addressVerificationStatus "valid") (not this.hasSuggestedAddress))
                  }}
                    <HStack class="items-center" data-test="address-verification-valid">
                      <PhCheck @size="16" class="tio-fg-success" />
                      <Text @variant="muted">{{t
                          "pslf.start_new.address_verification.verified"
                        }}</Text>
                    </HStack>
                  {{else if
                    (and
                      (or this.hasSubmitted this.hasBlurredAddressField)
                      (or
                        (eq this.addressVerificationStatus "invalid")
                        (eq this.addressVerificationStatus "unverified")
                        this.hasSuggestedAddress
                      )
                    )
                  }}
                    <AlertBanner @intent="warning" data-test="address-verification-warning">
                      {{t "pslf.start_new.address_verification.warning"}}
                    </AlertBanner>
                    {{#if this.hasSuggestedAddress}}
                      <VStack
                        class="rounded-md border tio-border-default p-4"
                        data-test="address-verification-suggestion"
                      >
                        <Text @variant="muted">
                          {{t "pslf.start_new.address_verification.did_you_mean"}}
                        </Text>
                        <Text @weight="medium" data-test="suggested-address-text">
                          {{this.formattedSuggestedAddress}}
                        </Text>
                        <HStack>
                          <Button
                            @intent="primary"
                            @appearance="outlined"
                            {{on "click" this.acceptSuggestedAddress}}
                            data-test="use-suggested-address"
                          >
                            {{t "pslf.start_new.address_verification.use_suggested"}}
                          </Button>
                          <Button
                            @appearance="outlined"
                            {{on "click" this.dismissSuggestion}}
                            data-test="keep-my-address"
                          >
                            {{t "pslf.start_new.address_verification.keep_mine"}}
                          </Button>
                        </HStack>
                      </VStack>
                    {{/if}}
                  {{/if}}
                {{/if}}
              </VStack>
            </:body>
          </Section>
        {{/each-in}}
        {{#if this.showPastPeriodsSection}}
          <Section class="border-0" data-test="past-periods-section">
            <:header>{{t "pslf.start_new.past_employment_periods"}}</:header>
            <:actions>
              {{#if this.canAddMorePeriods}}
                <Button
                  @intent="primary"
                  @appearance="minimal"
                  {{on "click" this.openAddPeriodDrawer}}
                  data-test="btn-add-past-period"
                >
                  <PhPlus @size={{24}} class="mr-2" />
                  {{t "pslf.start_new.add_past_period"}}
                </Button>
              {{/if}}
            </:actions>
            <:body>
              <VStack>
                {{#unless this.pastPeriods.length}}
                  <Text @variant="muted" @tag="p">
                    {{t "pslf.start_new.no_past_periods"}}
                  </Text>
                {{/unless}}
                {{#each this.pastPeriods as |period index|}}
                  <Section>
                    <:header>
                      {{period.pslfGroup.employerCommonName}}
                      -
                      {{period.pslfGroup.employerFein}}
                    </:header>
                    <:actions>
                      <Button
                        @intent="primary"
                        @appearance="minimal"
                        {{on "click" (fn this.openEditPeriodDrawer period)}}
                        data-test="btn-edit-period-{{index}}"
                        aria-label={{t "common.edit"}}
                      >
                        <PhPencil @size={{18}} />
                      </Button>
                      <Button
                        @intent="danger"
                        @appearance="minimal"
                        {{on "click" (fn this.removePastPeriod period.id)}}
                        data-test="btn-remove-period-{{index}}"
                        aria-label={{t "common.remove"}}
                      >
                        <PhTrash @size={{18}} />
                      </Button>
                    </:actions>
                    <:body>
                      <VStack class="gap-4">
                        <Text @variant="muted" @size="sm" @tag="span">
                          {{safeFormatDate
                            period.employmentStartDate
                            month="long"
                            day="2-digit"
                            year="numeric"
                            timeZone="UTC"
                          }}
                          -
                          {{safeFormatDate
                            period.employmentEndDate
                            month="long"
                            day="2-digit"
                            year="numeric"
                            timeZone="UTC"
                          }}
                        </Text>
                        <Text @variant="muted" @size="sm" @tag="span">
                          {{period.employmentHours}}
                          {{t "pslf.start_new.hours_per_week"}}
                        </Text>
                      </VStack>
                    </:body>
                  </Section>
                {{/each}}
              </VStack>
            </:body>
          </Section>
        {{/if}}
        <Section class="border-0">
          <:header>{{t "pslf.start_new.sign_your_pslf_form"}}</:header>
          <:body>
            <VStack>
              <div class="my-2">
                <span class="text-danger font-semibold mr-1">*</span>{{t
                  "pslf.start_new.required_items"
                }}
              </div>
              <p class="font-semibold -mb-2">{{t "signature"}}
                <span class="text-danger">*</span>
              </p>
              <div class="border rounded-2xl p-4 max-w-md">
                {{#if this.hasExistingSignature}}
                  <VStack class="items-center">
                    <VStack class="items-center tio-fg-info">
                      <PhCheck @size="20" />
                      <span class="font-semibold">
                        {{#if this.signaturePoints.length}}
                          {{t "pslf.start_new.signature_added"}}
                        {{else}}
                          {{t "pslf.start_new.saved_signature"}}
                        {{/if}}
                      </span>
                    </VStack>
                    <img
                      src={{@employee.pslfProfile.signature}}
                      alt={{t "signature_preview"}}
                      class="max-w-md max-h-32"
                      data-test="signature-preview"
                    />
                    <Button
                      @intent="primary"
                      @appearance="outlined"
                      {{on "click" this.openSignatureModal}}
                      data-test="btn-change-signature"
                    >
                      {{t "pslf.sign.update_signature"}}
                    </Button>
                  </VStack>
                {{else}}
                  <VStack class="items-center">
                    <PhPencilSimple @size="32" />
                    <p class="tio-fg-secondary font-medium">{{t
                        "pslf.start_new.no_signature_yet"
                      }}</p>
                    <Button
                      @intent="primary"
                      @appearance="outlined"
                      {{on "click" this.openSignatureModal}}
                      data-test="btn-add-signature"
                    >
                      {{t "pslf.start_new.add_signature"}}
                    </Button>
                  </VStack>
                {{/if}}
              </div>
              {{#if this.signatureFieldErrors}}
                <div class="tio-fg-danger" data-test="signature-field-errors">
                  {{#each this.signatureFieldErrors as |error|}}
                    <p>{{error}}</p>
                  {{/each}}
                </div>
              {{/if}}
            </VStack>
          </:body>
        </Section>
        <div class="text-center">
          <Button
            @intent="primary"
            @isRunning={{this.onSubmit.isRunning}}
            @type="submit"
            data-test="btn-submit-form-v2"
          >
            {{t "common.submit"}}
          </Button>
        </div>
      </VStack>
    </Form>
    <Modal @isOpen={{this.isOpenSignatureModal}} @onClose={{this.closeSignatureModal}} @size="xl">
      <:header>{{t "pslf.start_new.sign_your_pslf_form"}}</:header>
      <:body>
        <SignaturePad
          @onSignatureUpdate={{this.onSignatureUpdate}}
          @errors={{this.signatureFieldErrors}}
        />
      </:body>
      <:footer>
        <Footer
          @isRunning={{false}}
          @onCancel={{this.closeSignatureModal}}
          @onSubmit={{this.captureSignature}}
          @submitText={{t "sign"}}
        />
      </:footer>
    </Modal>
    <Drawer
      @isOpen={{this.isOpenPastPeriodDrawer}}
      @onClose={{this.closePastPeriodDrawer}}
      @closeOnOutsideClick={{false}}
      @size="lg"
    >
      <:header>
        <VStack>
          <span class="tio-text-xl-semibold">
            {{if
              this.isEditingExistingPeriod
              (t "pslf.start_new.edit_past_period_drawer_title")
              (t "pslf.start_new.add_past_period_drawer_title")
            }}
          </span>
          <Text @variant="muted" @size="sm" @tag="p">
            {{t "pslf.start_new.add_past_period_drawer_description"}}
          </Text>
        </VStack>
      </:header>
      <:body>
        <Form @data={{this.drawerFormData}} @onSubmit={{this.handleDrawerFormSubmit}}>
          <VStack class="pt-0">
            <Section class="p-4" data-test="drawer-option-select-group">
              <ReSelect
                @label={{t "pslf.start_new.select_employer"}}
                @items={{this.groupOptions}}
                @onChange={{this.selectDrawerGroup}}
                @selected={{if this.editingPeriodGroup this.editingPeriodGroup.id ""}}
                @errors={{this.editingPeriodGroupError}}
                @clearable={{false}}
                data-test="drawer-select-group"
              />
            </Section>
            <Section>
              <VStack>
                <Text @variant="muted" @size="sm" class="italic">
                  {{t "pslf.start_new.past_period_dates_hint"}}
                </Text>
                <Input
                  @name="employmentStartDate"
                  @label={{t "pslf.start_new.employment_start_date"}}
                  @type="date"
                  @value={{this.drawerFormData.employmentStartDate}}
                  @onChange={{fn this.updateDrawerField "employmentStartDate"}}
                  @isRequired={{true}}
                  @errors={{this.drawerFormErrors.employmentStartDate}}
                  max={{this.todayDateString}}
                  data-test="drawer-input-start-date"
                />
                <Input
                  @name="employmentEndDate"
                  @label={{t "pslf.start_new.employment_end_date"}}
                  @type="date"
                  @value={{this.drawerFormData.employmentEndDate}}
                  @onChange={{fn this.updateDrawerField "employmentEndDate"}}
                  @isRequired={{true}}
                  @errors={{this.drawerFormErrors.employmentEndDate}}
                  min={{or this.drawerFormData.employmentStartDate ""}}
                  max={{this.todayDateString}}
                  data-test="drawer-input-end-date"
                />
                <Input
                  @name="employmentHours"
                  @label={{t "pslf.start_new.employment_hours"}}
                  @type="number"
                  @value={{this.drawerFormData.employmentHours}}
                  @onChange={{fn this.updateDrawerField "employmentHours"}}
                  @isRequired={{true}}
                  @errors={{this.drawerFormErrors.employmentHours}}
                  data-test="drawer-input-hours"
                />
              </VStack>
            </Section>
            {{#if this.overlapResults.length}}
              <AlertBanner @intent="warning" data-test="drawer-overlap-warning">
                <VStack class="gap-2 p-0">
                  <Text @variant="muted" @tag="p" @size="sm" @weight="semibold">
                    {{t "pslf.overlap_warning.description" count=this.overlapResults.length}}
                  </Text>
                  {{#each this.overlapResults as |overlap|}}
                    <div class="rounded border tio-border-warning tio-bg-surface px-3 py-2">
                      <div class="tio-row-flush">
                        <Text @size="sm">
                          {{or
                            overlap.period.employerName
                            (t "pslf.overlap_warning.unknown_employer")
                          }}
                        </Text>
                        <Text @variant="muted" @size="sm" class="ml-1">
                          {{safeFormatDate
                            overlap.period.startDate
                            month="short"
                            day="2-digit"
                            year="numeric"
                            timeZone="UTC"
                          }}
                          -
                          {{#if overlap.period.endDate}}
                            {{safeFormatDate
                              overlap.period.endDate
                              month="short"
                              day="2-digit"
                              year="numeric"
                              timeZone="UTC"
                            }}
                          {{else}}
                            {{t "pslf.overlap_warning.present"}}
                          {{/if}}
                        </Text>
                      </div>
                      {{#if overlap.period.status}}
                        <div class="mt-1">
                          <Text @variant="muted" @size="xs">
                            {{t (concat "common.pslf.status." overlap.period.status)}}
                          </Text>
                        </div>
                      {{/if}}
                    </div>
                  {{/each}}
                </VStack>
              </AlertBanner>
            {{/if}}
          </VStack>
          <HStack class="gap-2 justify-end mt-6">
            <Button
              @intent="default"
              @appearance="outlined"
              {{on "click" this.closePastPeriodDrawer}}
              data-test="drawer-btn-cancel"
            >
              {{t "common.cancel"}}
            </Button>
            <Button @intent="primary" @type="submit" data-test="drawer-btn-save">
              {{t "common.save"}}
            </Button>
          </HStack>
        </Form>
      </:body>
    </Drawer>
  </template>
}
